import { c as _c } from "react/compiler-runtime";
import { LucideIcon } from 'lucide-react';
import React, { PropsWithChildren } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/comps/primitives/popover';
import { UIPopoverMenuItem } from '@/comps/ui/menu/UIPopoverMenuItem';
import { PopoverContentProps } from '@radix-ui/react-popover';
import { cn } from '@/utils/shad';
import { WithBaseElementGlobalAttributes, WithIcon, WithOnClick, WithRenderPropChildren, WithValue } from '@/types/component';
import { Button } from '@/comps/primitives/button';
export type UIMenuOption = WithOnClick & WithValue<string> & {
  icon?: LucideIcon;
  label: string;
  show?: boolean;
  separator?: boolean;
  tooltipText?: string | null;
};
export type UIPopoverMenuProps = Omit<UIPopoverMenuContentProps, 'children'> & WithRenderPropChildren<{
  options: UIMenuOption[];
}> & WithIcon & {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: React.ReactNode;
  options?: UIMenuOption[];
  align?: PopoverContentProps['align'];
  side?: PopoverContentProps['side'];
  label?: string;
};
export function UIPopoverMenu(t0) {
  const $ = _c(24);
  const {
    open: t1,
    onOpenChange,
    trigger,
    options,
    label: t2,
    align: t3,
    side,
    blur,
    children,
    className,
    style,
    id,
    icon
  } = t0;
  const open = t1 === undefined ? true : t1;
  const label = t2 === undefined ? "Menu" : t2;
  const align = t3 === undefined ? "end" : t3;
  let t4;
  if ($[0] !== icon || $[1] !== label || $[2] !== trigger) {
    t4 = trigger ? trigger : <Button PrependIcon={icon} variant="outline">{label}</Button>;
    $[0] = icon;
    $[1] = label;
    $[2] = trigger;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t4) {
    t5 = <PopoverTrigger className="flex min-h-4 min-w-4 items-center" asChild={true}>{t4}</PopoverTrigger>;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== children || $[7] !== options) {
    t6 = children ? typeof children === "function" ? children({
      options: options ?? []
    }) : <>{children}</> : <ul className="flex flex-col gap-1 p-1">{options?.map(_temp)}</ul>;
    $[6] = children;
    $[7] = options;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== blur || $[10] !== className || $[11] !== id || $[12] !== style || $[13] !== t6) {
    t7 = <UIPopoverMenuContent blur={blur} className={className} style={style} id={id}>{t6}</UIPopoverMenuContent>;
    $[9] = blur;
    $[10] = className;
    $[11] = id;
    $[12] = style;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== align || $[16] !== side || $[17] !== t7) {
    t8 = <PopoverContent data-tour="popover-menu-content" className="w-auto min-w-0 border-0 bg-transparent p-0 shadow-none" align={align} side={side}>{t7}</PopoverContent>;
    $[15] = align;
    $[16] = side;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== onOpenChange || $[20] !== open || $[21] !== t5 || $[22] !== t8) {
    t9 = <Popover open={open} onOpenChange={onOpenChange}>{t5}{t8}</Popover>;
    $[19] = onOpenChange;
    $[20] = open;
    $[21] = t5;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  return t9;
}
function _temp(t0, i) {
  const {
    icon: icon_0,
    label: label_0,
    onClick
  } = t0;
  return <UIPopoverMenuItem key={i} icon={icon_0} onClick={onClick}>{label_0}</UIPopoverMenuItem>;
}
type UIPopoverMenuContentProps = PropsWithChildren & WithBaseElementGlobalAttributes & {
  // in pixels
  blur?: number;
};
export function UIPopoverMenuContent(t0) {
  const $ = _c(14);
  const {
    className,
    style,
    id,
    blur,
    children
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("max-h-90 w-auto min-w-0 overflow-x-hidden overflow-y-auto rounded-lg border bg-background shadow-md", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== blur) {
    t2 = blur != null ? {
      backdropFilter: `blur(${blur}px)`
    } : {};
    $[2] = blur;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== style || $[5] !== t2) {
    t3 = {
      ...t2,
      ...style
    };
    $[4] = style;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== children) {
    t4 = <>{children}</>;
    $[7] = children;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== id || $[10] !== t1 || $[11] !== t3 || $[12] !== t4) {
    t5 = <div className={t1} style={t3} id={id}>{t4}</div>;
    $[9] = id;
    $[10] = t1;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}